///
/// EarthBrowser
///
/// React hook for querying current air quality at a point from the
/// CAMS air quality grid.
///
/// Created by Matt Giger on September 15, 2026
/// Copyright (c) 2026 Geodata Labs
///

import { useEffect, useRef, useState } from "react"
import type { AirQualityInfo } from "./AirQualityCompute"
import { getAirQualityGridService } from "./AirQualityGridService"

interface AirQualityQueryResult {
	data: AirQualityInfo | undefined
	isLoading: boolean
	isError: boolean
	error?: unknown
}

export function useCurrentAirQuality(lat: number | null, lon: number | null): AirQualityQueryResult {
	const [data, setData] = useState<AirQualityInfo | undefined>(undefined)
	const [isLoading, setIsLoading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [error, setError] = useState<unknown>(undefined)
	const abortRef = useRef(0)

	useEffect(() => {
		if (lat === null || lon === null) {
			setData(undefined)
			return
		}

		const requestId = ++abortRef.current
		setIsLoading(true)
		setIsError(false)

		const service = getAirQualityGridService()
		service.getCurrentAirQuality(lat, lon)
			.then(result => {
				if (requestId !== abortRef.current) return
				setData(result ?? undefined)
				setIsLoading(false)
			})
			.catch(err => {
				if (requestId !== abortRef.current) return
				setIsError(true)
				setError(err)
				setIsLoading(false)
			})
	}, [lat, lon])

	return { data, isLoading, isError, error }
}
